import { useMemo, useRef, useCallback } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
} from 'chart.js';
import PropTypes from 'prop-types';
import 'chartjs-plugin-style';
import { getTooltipLabel } from 'helpers/charts';
import { useChartSupportValues } from 'hooks/useChartSupportValues';
import { useSimpleMedia } from 'hooks/useMedia';
import commentImage from 'assets/icons/comment.png';
import { getMonthIndexFromName } from 'helpers/monthSelector';
import { HR_TABS_IDS } from '../../../const/HRPage';

ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler
);

const { CA, TOTAL_COST } = HR_TABS_IDS;

const colors = {
  0: 'rgba(10,169,188,1)',
  1: 'rgba(162,205,111,1)',
  2: 'rgba(184,184,184,1)',
  3: 'rgba(0,0,0,1)', // fourth color is no needed
};

const transparentColors = {
  0: 'rgba(10,169,188,0.1)',
  1: 'rgba(162,205,111,0.1)',
  2: 'rgba(184,184,184,0.1)',
  3: 'rgba(0,0,0,0.1)', // fourth color is no needed
};

const SCREEN_WIDTH_BREAKPOINT_VALUE = 950;
const SCREEN_WIDTH_BREAKPOINT_MEDIA = `(max-width: ${SCREEN_WIDTH_BREAKPOINT_VALUE}px)`;

const propTypes = {
  data: PropTypes.array.isRequired,
  hiddenLinesYears: PropTypes.array,
  activeMonthName: PropTypes.string.isRequired,
  activeYear: PropTypes.number.isRequired,
  activeDateViewOption: PropTypes.string.isRequired,
  firstMonth: PropTypes.string.isRequired,
  enableGrid: PropTypes.bool,
  comments: PropTypes.array,
  openCommentsModal: PropTypes.func,
  dashboardName: PropTypes.string,
  activeTabId: PropTypes.string,
  showLastDayOfMonthOnTooltipInAnnualMode: PropTypes.bool,
};

const commentsImage = new Image();
commentsImage.src = commentImage;
commentsImage.width = 20;
commentsImage.height = 20;

/**
 * @param data {array} - data for chart with following
 *                      structure [{year:number, values:Array<number>>}]
 *                  if its Year View values starts from firstmonth
 *                  e.g first month sept -> values [ 0,  1,  2, ... , 11]
 *                                                 sep, oct,nov,      aug
 * @param hiddenLinesYears {array<Number>}
 * @param activeMonthName {string} - active month in MonthSelector from January to December
 * @param activeYear {number}
 * @param activeDateViewOption {string} - date mode either Annuels(YEAR) or Mensuels(MONTH) from const
 * @param firstMonth {string} - name of first month of financial year from January to December
 * @param showLastDayOfMonthOnTooltipInAnnualMode -  when enabled: instead of Month Year label will be dd.mm.yyyy in tooltip
 *                                                    e.g. instead Fevr 2019  -> 28.02.2019
 * @param comments {array} - array same as values of data above, but if comments exist value 0, if not value null
 * @param enableGrid {boolean} - is grid enabled
 * @param dashboardName {string} - one of 'treasury' 'turnover' 'debtors' 'creditors'
 * @param activeTabId {string} - current tab name, default ''
 * @param openCommentsModal {func} -> function that setup comments modal and opens it
 * */
export const LineChart = ({
  data = [],
  hiddenLinesYears,
  activeMonthName,
  activeYear,
  activeDateViewOption,
  firstMonth,
  enableGrid = false,
  comments = [],
  openCommentsModal = () => {},
  dashboardName = '',
  activeTabId = '',
  showLastDayOfMonthOnTooltipInAnnualMode = false,
  ...restProps
}) => {
  const lineChartRef = useRef(null);
  const { labels, setHoveredLineIndex, hoveredLineIndex, isViewModeAnnual } =
    useChartSupportValues(
      activeDateViewOption,
      activeYear,
      activeMonthName,
      firstMonth
    );

  const isMobileVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);

  const dataWithComments = [{}, ...data];

  const minValueOfChartData = useMemo(() => {
    let min = 0;
    data.forEach(({ values }) => {
      values.forEach((value) => {
        if (value < min) min = value;
      });
    });
    return min;
  }, [data]);

  const commentChartValue = useMemo(() => {
    const needToAddTo1000 =
      minValueOfChartData % 1000 === 0
        ? 0
        : 1000 - Math.abs(minValueOfChartData % 1000);
    return minValueOfChartData - needToAddTo1000;
  }, [minValueOfChartData]);

  const chartData = {
    labels,
    datasets: dataWithComments.map(({ id, year, values }, index) => {
      if (hiddenLinesYears.includes(year)) return { label: id, hidden: true };
      if (index === 0) {
        return {
          hidden: !!isMobileVersion,
          label: `comments${activeDateViewOption}${dashboardName}`,
          type: 'line',
          data: labels.map(() => commentChartValue),
          borderWidth: 10,
          pointStyle: commentsImage,
          pointBackgroundColor: labels.map((comment, commentIndex) => {
            if (comments[commentIndex] === 0) return 'rgba(0,0,0,1)';
            return 'rgba(0,0,0,0)';
          }),
          borderColor: 'transparent',
          backgroundColor: 'red',
          pointRadius: 0,
          pointHoverRadius: 10,
          pointHoverBackgroundColor: 'white',
          pointBorderColor: 'white',
        };
      }
      return {
        label: id,
        data: values,
        borderWidth: 2,
        borderColor: colors[index - 1],
        backgroundColor: transparentColors[index - 1],
        pointHoverBackgroundColor: colors[index - 1],
        hoverBorderColor: 'white',
        pointBackgroundColor:
          values.filter((value) => value !== null).length > 1
            ? 'transparent'
            : colors[index - 1],
        pointBorderColor:
          values.filter((value) => value !== null).length > 1
            ? 'transparent'
            : 'white',
        fill: index === 1,
        pointRadius: isMobileVersion ? 3 : 6,
        pointHoverRadius: isMobileVersion ? 3 : 6,
        pointShadowOffsetX: 0,
        pointShadowOffsetY: 0,
        pointShadowBlur: 2,
        pointShadowColor: 'rgba(0, 0, 0, 0.5)',
        spanGaps: true,
        tension: 0.4,
      };
    }),
  };

  const onClick = useCallback(
    (e) => {
      const [elem] = lineChartRef.current.getElementsAtEventForMode(
        e,
        'nearest',
        { intersect: true },
        false
      );

      if (elem && elem.datasetIndex === 0) {
        if (labels.length === 12) {
          const monthDependsOfFirstMonth = elem.index;
          const firstMonthIndex = getMonthIndexFromName(firstMonth);
          const month =
            firstMonthIndex + monthDependsOfFirstMonth > 11
              ? firstMonthIndex + monthDependsOfFirstMonth - 12
              : firstMonthIndex + monthDependsOfFirstMonth;
          openCommentsModal({
            month,
            day: null,
            chartType: dashboardName.toLowerCase(),
            dateMode: 'month',
          });
        } else {
          const day = elem.index + 1;
          const month = getMonthIndexFromName(activeMonthName);
          openCommentsModal({
            month,
            day,
            chartType: dashboardName.toLowerCase(),
            dateMode: 'day',
          });
        }
      }
    },
    [
      activeMonthName,
      dashboardName,
      firstMonth,
      labels.length,
      openCommentsModal,
    ]
  );

  return (
    <Chart
      ref={lineChartRef}
      type="line"
      {...restProps}
      data={chartData}
      options={{
        events: ['mousemove', 'click'],
        onClick,
        maintainAspectRatio: false,
        responsive: true,
        animation: {
          duration: 0,
        },
        hover: {
          mode: 'point',
          intersect: false,
        },
        onHover(e, item) {
          e.native.target.style.cursor = item[0] ? 'pointer' : 'default';
          if (item.length) {
            setHoveredLineIndex(item[0].datasetIndex);
          } else {
            setHoveredLineIndex(null);
          }
        },
        scales: {
          x: {
            position: data.every((dataset) =>
              dataset.values.every((value) => value <= 0)
            )
              ? 'top'
              : 'bottom',
            grid: {
              color: enableGrid ? 'rgba(0,0,0,0.05)' : 'rgba(0, 0, 0, 0)',
            },
            ticks: {
              color: '#959595',
              font: {
                family: 'Roboto, sans-serif',
                size: 13,
              },
              // maxRotation: 0,
              // autoSkipPadding: 4,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: '#959595',
              font: {
                family: 'Roboto, sans-serif',
                size: 14,
              },
              callback(value) {
                if (value === 0) return `${value}  `;
                if (value === 1) return '';
                const currencySign =
                  ['TURNOVER', 'TREASURY', 'DEBTORS', 'CREDITORS'].includes(
                    dashboardName
                  ) || [CA, TOTAL_COST].includes(activeTabId)
                    ? '€'
                    : '';
                return `${Math.round(value / 1000)} K${currencySign}`;
              },
              stepSize: function (context) {
                const maxValue = context.chart.scales.y.max;
                const minValue = context.chart.scales.y.min;
                const range = maxValue - minValue;

                if (range > 1000000) return 250000;
                if (range > 100000) return 50000;
                if (range > 10000) return 5000;
                if (range > 1000) return 500;
                return 100;
              },

              maxTicksLimit: 10,
            },
          },
        },

        legend: {
          display: false,
        },

        plugins: {
          tooltip: {
            enabled: hoveredLineIndex !== 0,
            mode: 'x',
            position: 'nearest',
            bodySpacing: 5,
            bodyFontFamily: 'Roboto, sans-serif',
            bodyFontSize: 13,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowBlur: 5,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            cornerRadius: 2,
            callbacks: {
              labelColor(item) {
                const index = item.datasetIndex;
                if (index === 0) return '';
                const color = colors[index - 1];
                return {
                  borderColor: color,
                  backgroundColor: color,
                };
              },
              label(item) {
                if (item.datasetIndex === 0) return '';
                return getTooltipLabel(
                  item,
                  activeMonthName,
                  activeYear,
                  dataWithComments,
                  isViewModeAnnual,
                  showLastDayOfMonthOnTooltipInAnnualMode,
                  firstMonth
                );
              },
              labelTextColor() {
                return '#ffffff';
              },
              title() {
                return '';
              },
            },
          },
        },
      }}
    />
  );
};

LineChart.propTypes = propTypes;
